import React, { useEffect, useRef, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { brandSrc, refreshBranding } from "@/components/BrandLogo";
import { UploadCloud, Save } from "lucide-react";

export default function AdminBranding() {
  const [b, setB] = useState(null);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [logoFileId, setLogoFileId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/branding");
      setB(data);
      setName(data.business_name);
      setTagline(data.tagline);
      setPreview(brandSrc(data.logo_url));
    } catch {
      const d = { logo_url: "/arps-logo.png", business_name: "Audit Ready Process Service", tagline: "Every Service. Every Attempt. Every Detail. Documented." };
      setB(d); setName(d.business_name); setTagline(d.tagline); setPreview(brandSrc(d.logo_url));
    }
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image (PNG, JPG, or WEBP)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoFileId(data.id);
      setPreview(URL.createObjectURL(f));
      toast.success("Logo uploaded — click Save branding to apply.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Upload failed"));
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { business_name: name, tagline };
      if (logoFileId) body.logo_file_id = logoFileId;
      const { data } = await api.put("/admin/branding", body);
      setB(data);
      setLogoFileId(null);
      refreshBranding(data);
      setPreview(brandSrc(data.logo_url));
      toast.success("Branding saved.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Save failed"));
    } finally { setSaving(false); }
  };

  if (!b) return null;

  return (
    <div className="mx-auto max-w-2xl" data-testid="admin-branding-page">
      <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Branding</h1>
      <p className="mt-1 text-sm text-slate-500">Manage the ARPS logo, business name, and tagline shown across the client-facing experience.</p>

      <div className="mt-6 space-y-6 rounded-xl border border-slate-200 bg-white p-6">
        <div>
          <Label>Logo</Label>
          <div className="mt-2 flex items-center gap-5">
            <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-2">
              {preview
                ? <img data-testid="branding-logo-preview" src={preview} alt="Logo preview" className="h-full w-auto object-contain" />
                : <span className="text-xs text-slate-400">No logo</span>}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="branding-logo-input" />
              <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="branding-upload-button">
                <UploadCloud className="mr-2 h-4 w-4" />{uploading ? "Uploading…" : "Upload logo"}
              </Button>
              <p className="mt-1.5 text-xs text-slate-400">PNG, JPG, or WEBP. A wide or square transparent PNG works best.</p>
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="bn">Business name</Label>
          <Input id="bn" data-testid="branding-name-input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="tg">Tagline</Label>
          <Input id="tg" data-testid="branding-tagline-input" value={tagline} onChange={(e) => setTagline(e.target.value)} />
        </div>

        <Button onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="branding-save-button">
          <Save className="mr-2 h-4 w-4" />{saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </div>
  );
}
